import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { CardAdvanceListRow } from '../../../../core/models/card-quota/card-advance.model';
import { MapperCardAdvanceService } from '../../../../core/mappers/card-quota/mapper-card-advance.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadActivateCodesExcel } from '../../../../core/utils/activate-codes-excel-export.util';
import { ModalCardAdvanceComponent } from '../../modals/card-quota/modal-card-advance/modal-card-advance.component';
import { ModalCardAdvanceDetailComponent } from '../../modals/card-quota/modal-card-advance-detail/modal-card-advance-detail.component';

@Component({
  selector: 'app-card-advances',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalCardAdvanceComponent,
    ModalCardAdvanceDetailComponent,
  ],
  templateUrl: './card-advances.component.html',
  styleUrl: './card-advances.component.css',
})
export class CardAdvancesComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperCardAdvanceService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<CardAdvanceListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  isDetailModalOpen = signal(false);
  isExecuteConfirmOpen = signal(false);
  isExporting = signal(false);
  executeTarget = signal<CardAdvanceListRow | null>(null);
  selectedAdvanceId = signal(0);
  detailAdvanceId = signal(0);
  filterStatus = signal<number | ''>('');

  dateFrom = signal(this.defaultDateFrom());
  dateTo = signal(this.defaultDateTo());

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  readonly statusOptions = [
    { value: '' as const, label: 'ทุกสถานะ' },
    { value: 1, label: 'Draft' },
    { value: 4, label: 'รออนุมัติ' },
    { value: 2, label: 'อนุมัติแล้ว' },
    { value: 3, label: 'สร้างรหัสแล้ว' },
    { value: 5, label: 'ปฏิเสธ' },
    { value: 6, label: 'ยกเลิก' },
  ];

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadScopeAndTable();
      });
    });
  }

  get currentUserId(): number {
    return Number(this.cookieService.get('user_id') || 0);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get initialClientIdForModal(): number {
    return this.clientScope.lockedClientId();
  }

  statusLabel = (s: number) => this.mapper.statusLabel(s);

  statusBadgeClass(status: number): string {
    const map: Record<number, string> = {
      1: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
      2: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      3: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      4: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      5: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
      6: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    };
    return map[status] ?? 'bg-slate-100 text-slate-600';
  }

  trackById(_: number, item: CardAdvanceListRow): number {
    return item.id;
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadScopeAndTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadScopeAndTable(true);
  }

  onDateChange(): void {
    if (!this.validateDateRange()) return;
    this.currentPage.set(1);
    this.loadTable();
  }

  onFilterChange(): void {
    if (!this.validateDateRange()) return;
    this.currentPage.set(1);
    this.loadTable();
  }

  applyPresetDays(days: number): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    this.dateTo.set(this.toDateInput(to));
    this.dateFrom.set(this.toDateInput(from));
    this.onDateChange();
  }

  applyThisMonth(): void {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dateFrom.set(this.toDateInput(from));
    this.dateTo.set(this.toDateInput(now));
    this.onDateChange();
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    this.pageSize.set(value);
    this.currentPage.set(1);
    this.loadTable();
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (this.clientScope.isRestricted() && this.clientScope.state()?.clients.length === 0) {
      this.toast.error('ไม่มีร้านที่คุณเข้าถึงได้ในลานนี้');
      return;
    }
    this.selectedAdvanceId.set(0);
    this.isModalOpen.set(true);
  }

  openEdit(item: CardAdvanceListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (item.status !== 1) {
      this.toast.error('แก้ไขได้เฉพาะสถานะ Draft');
      return;
    }
    this.selectedAdvanceId.set(item.id);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedAdvanceId.set(0);
  }

  openDetail(item: CardAdvanceListRow): void {
    this.detailAdvanceId.set(item.id);
    this.isDetailModalOpen.set(true);
  }

  closeDetailModal(): void {
    this.isDetailModalOpen.set(false);
    this.detailAdvanceId.set(0);
  }

  execute(item: CardAdvanceListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (item.status !== 2) return;
    this.executeTarget.set(item);
    this.isExecuteConfirmOpen.set(true);
  }

  exportExcel(item: CardAdvanceListRow): void {
    if (item.status !== 3) return;
    this.isExporting.set(true);
    this.api.getQuotaCardAdvanceActivateCodes(item.id).subscribe({
      next: (res) => {
        const rows = this.mapper.mapActivateCodes(res ?? []);
        if (rows.length === 0) {
          this.toast.error('ไม่พบรหัส activate');
          this.isExporting.set(false);
          return;
        }
        downloadActivateCodesExcel(rows, {
          advanceId: item.id,
          advanceName: item.name,
          siteName: this.siteState.selectedSite()?.name ?? null,
          clientName: item.clientName,
          vehicleTypeName: item.vehicleTypeName,
          cardTypeName: item.cardTypeName,
          qty: item.qty,
          startDate: item.startDate,
          endDate: item.endDate,
        })
          .then(() => this.toast.success('ส่งออก Excel สำเร็จ'))
          .catch(() => this.toast.error('ส่งออก Excel ล้มเหลว'))
          .finally(() => this.isExporting.set(false));
      },
      error: (err) => {
        this.isExporting.set(false);
        this.toast.error(err?.error?.message || 'ส่งออก Excel ล้มเหลว');
      },
    });
  }

  closeExecuteConfirm(): void {
    this.isExecuteConfirmOpen.set(false);
    this.executeTarget.set(null);
  }

  get executeQtyHighlight(): string {
    const qty = this.executeTarget()?.qty;
    return qty != null ? String(qty) : '';
  }

  confirmExecuteAdvance(): void {
    const item = this.executeTarget();
    if (!item) return;

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url);

    this.closeExecuteConfirm();
    this.isLoading.set(true);

    this.api.executeCardAdvance(this.mapper.buildActionPayload(item.id, userId, menuId)).subscribe({
      next: (res) => {
        if (res.code === '000' || res.isSuccess) {
          this.toast.success(res.message || 'สร้างรหัสสำเร็จ');
          this.loadTable();
        } else {
          this.toast.error(res.message || 'สร้างรหัสล้มเหลว');
          this.isLoading.set(false);
        }
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'สร้างรหัสล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    const end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadScopeAndTable(forceScope = false): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.clientScope.load(siteId, this.currentUserId, forceScope).subscribe({
      next: () => this.loadTable(),
      error: () => this.loadTable(),
    });
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }
    if (!this.validateDateRange()) {
      this.isLoading.set(false);
      return;
    }

    const statusFilter = this.filterStatus();
    this.isLoading.set(true);
    this.api
      .getCardAdvancesForTable(
        siteId,
        this.currentPage(),
        this.pageSize(),
        null,
        statusFilter === '' ? null : statusFilter,
        this.dateFrom(),
        this.dateTo()
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดรายการบัตรล่วงหน้าล้มเหลว');
        },
      });
  }

  private validateDateRange(): boolean {
    if (!this.dateFrom() || !this.dateTo()) {
      this.toast.error('กรุณาเลือกช่วงวันที่สร้าง');
      return false;
    }
    if (this.dateFrom() > this.dateTo()) {
      this.toast.error('วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด');
      return false;
    }
    return true;
  }

  private defaultDateFrom(): string {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return this.toDateInput(d);
  }

  private defaultDateTo(): string {
    return this.toDateInput(new Date());
  }

  private toDateInput(d: Date): string {
    return d.toISOString().slice(0, 10);
  }
}
